import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { TRPCClientError } from '@trpc/client';
import type {
  Customer,
  Item,
  SalesOrderReportInput,
  SalesOrderReportResponse,
  SalesOrderReportRow,
  Vendor,
} from '@repo/api-contract';
import {
  Autocomplete,
  Box,
  Button,
  CircularProgress,
  Divider,
  FormControl,
  FormControlLabel,
  FormLabel,
  IconButton,
  Link,
  Paper,
  Radio,
  RadioGroup,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import {
  DataGridPro,
  GridColDef,
  GridRenderCellParams,
  useGridApiRef,
} from '@mui/x-data-grid-pro';
import SearchIcon from '@mui/icons-material/Search';
import CloseIcon from '@mui/icons-material/Close';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs, { Dayjs } from 'dayjs';
import DownloadOutlinedIcon from '@mui/icons-material/DownloadOutlined';
import client from '../../util/api-client';
import { Routes } from '../../util/constants';
import { pushToast, useDispatch } from '../../state';
import { serverError } from '../../components/modals/constants/errors';
import { openSearchCustomerModal } from '../../state/customerSearchModalSlice';
import { openSearchVendorModal } from '../../state/vendorSearchModalSlice';
import { openSearchItemModal } from '../../state/itemSearchModalSlice';
import {
  EMPTY_LEADING_GRID_COLUMNS,
  usePersistedDataGridColumnPreferences,
} from '../../util/hooks';
import type { DataGridTableId } from '../../util/dataGridTableIds';
import { DataGridTranslatedColumnHeader } from '../../components/dataGrid/DataGridTranslatedColumnHeader';
import hexToRGBA from '../../util/converters/hexToRGBA';

const TABLE_ID = 'sales-order-report' satisfies DataGridTableId;
const KEY_SALES_ORDER_REPORT_PAGE = 'team.Components.SalesOrderReportPage';
const ROWS_PER_PAGE_OPTIONS = [10, 25, 50, 100];

type ReportBy = NonNullable<SalesOrderReportInput['reportBy']>;
type DateFilterBy = NonNullable<SalesOrderReportInput['dateFilterBy']>;

const currencyFormatter = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
});

function dateInputToUnix(value: string, endOfDay = false): number | undefined {
  if (!value) return undefined;
  const [year = 0, month = 1, day = 1] = value.split('-').map(Number);
  const date = new Date(
    year,
    month - 1,
    day,
    endOfDay ? 23 : 0,
    endOfDay ? 59 : 0,
    endOfDay ? 59 : 0,
    endOfDay ? 999 : 0,
  );
  return Math.floor(date.getTime() / 1000);
}

function formatCurrency(value: number) {
  return currencyFormatter.format(value);
}

function formatDate(value: number) {
  return new Date(value * 1000).toLocaleDateString();
}

export default function SalesOrderReport() {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const apiRef = useGridApiRef();
  const { t } = useTranslation([], {
    keyPrefix: KEY_SALES_ORDER_REPORT_PAGE,
  });
  const [reportBy, setReportBy] = useState<ReportBy>('vendor');
  const [vendors, setVendors] = useState<Vendor[]>([]);
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [items, setItems] = useState<Item[]>([]);
  const [selectedVendor, setSelectedVendor] = useState<Vendor | null>(null);
  const [selectedCustomer, setSelectedCustomer] = useState<Customer | null>(
    null,
  );
  const [selectedItem, setSelectedItem] = useState<Item | null>(null);
  const [dateFilterBy, setDateFilterBy] = useState<DateFilterBy>('orderDate');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [report, setReport] = useState<SalesOrderReportResponse | null>(null);
  const [loadingEntities, setLoadingEntities] = useState(false);
  const [loadingReport, setLoadingReport] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [paginationModel, setPaginationModel] = useState({
    page: 0,
    pageSize: 10,
  });

  const handleError = useCallback(
    (error: unknown) => {
      if (error instanceof TRPCClientError) {
        dispatch(pushToast({ message: error.message, type: 'error' }));
      } else {
        dispatch(pushToast({ message: serverError, type: 'error' }));
      }
    },
    [dispatch],
  );

  const selectedQuery = useMemo(() => {
    if (reportBy === 'vendor' && selectedVendor) {
      return selectedVendor.code;
    }
    if (reportBy === 'customer' && selectedCustomer) {
      return selectedCustomer.code;
    }
    if (reportBy === 'item' && selectedItem) {
      return selectedItem.details.vendorItemNumber;
    }
    return undefined;
  }, [reportBy, selectedVendor, selectedCustomer, selectedItem]);

  const buildReportInput = useCallback(
    (page: number, pageSize: number): SalesOrderReportInput => ({
      reportBy,
      query: selectedQuery,
      dateFilterBy,
      dateFrom: dateInputToUnix(dateFrom),
      dateTo: dateInputToUnix(dateTo, true),
      size: pageSize,
      page,
    }),
    [reportBy, selectedQuery, dateFilterBy, dateFrom, dateTo],
  );

  const downloadInput = useMemo(
    () => ({
      reportBy,
      query: selectedQuery,
      dateFilterBy,
      dateFrom: dateInputToUnix(dateFrom),
      dateTo: dateInputToUnix(dateTo, true),
    }),
    [reportBy, selectedQuery, dateFilterBy, dateFrom, dateTo],
  );

  const loadReport = useCallback(
    async (input: SalesOrderReportInput) => {
      if (
        input.dateFrom != null &&
        input.dateTo != null &&
        input.dateFrom > input.dateTo
      ) {
        dispatch(
          pushToast({ message: t('From must be before To'), type: 'error' }),
        );
        return;
      }

      setLoadingReport(true);
      try {
        const response = await client.getSalesOrderReport.query(input);
        setReport(response);
      } catch (error) {
        handleError(error);
      } finally {
        setLoadingReport(false);
      }
    },
    [dispatch, handleError, t],
  );

  useEffect(() => {
    let cancelled = false;

    async function loadEntities() {
      setLoadingEntities(true);
      try {
        const [vendorResponse, customerResponse, itemResponse] =
          await Promise.all([
            client.searchVendors.query({
              query: '',
              searchBy: 'vendorName',
              size: 100,
              page: 0,
            }),
            client.searchCustomers.query({
              query: '',
              searchBy: 'companyName',
              size: 100,
              page: 0,
            }),
            client.searchItems.query({
              query: '',
              searchBy: 'vendorName',
              size: 100,
              page: 0,
            }),
          ]);
        if (!cancelled) {
          setVendors(vendorResponse.results);
          setCustomers(customerResponse.results);
          setItems(itemResponse.results);
        }
      } catch (error) {
        if (cancelled) return;
        handleError(error);
      } finally {
        if (!cancelled) {
          setLoadingEntities(false);
        }
      }
    }

    loadEntities();

    return () => {
      cancelled = true;
    };
  }, [handleError]);

  useEffect(() => {
    void loadReport(buildReportInput(0, paginationModel.pageSize));
    // Run the default unfiltered report once on page open.
  }, []);

  const rows = useMemo(
    (): SalesOrderReportRow[] => report?.results ?? [],
    [report],
  );

  const handleReportByChange = (value: ReportBy) => {
    setReportBy(value);
    setSelectedVendor(null);
    setSelectedCustomer(null);
    setSelectedItem(null);
  };

  const handleGenerate = () => {
    setPaginationModel((current) => ({ ...current, page: 0 }));
    loadReport(buildReportInput(0, paginationModel.pageSize));
  };

  const handleClearFilters = () => {
    setReportBy('vendor');
    setSelectedVendor(null);
    setSelectedCustomer(null);
    setSelectedItem(null);
    setDateFilterBy('orderDate');
    setDateFrom('');
    setDateTo('');
  };

  const handleDownload = async () => {
    if (!report) return;
    setDownloading(true);
    try {
      const response =
        await client.downloadSalesOrderReport.query(downloadInput);
      window.open(response.url, '_blank', 'noopener,noreferrer');
    } catch (error) {
      handleError(error);
    } finally {
      setDownloading(false);
    }
  };

  const baseColumns = useMemo(
    (): GridColDef[] => [
      {
        field: 'vendorCode',
        headerName: 'Vendor Code',
        flex: 1,
        minWidth: 120,
        renderHeader: (params) => (
          <DataGridTranslatedColumnHeader
            labelKey="Vendor Code"
            keyPrefix={KEY_SALES_ORDER_REPORT_PAGE}
            params={params}
          />
        ),
      },
      {
        field: 'vendorName',
        headerName: 'Vendor Name',
        flex: 1,
        minWidth: 160,
        renderHeader: (params) => (
          <DataGridTranslatedColumnHeader
            labelKey="Vendor Name"
            keyPrefix={KEY_SALES_ORDER_REPORT_PAGE}
            params={params}
          />
        ),
      },
      {
        field: 'status',
        headerName: 'Status',
        flex: 1,
        minWidth: 100,
        renderHeader: (params) => (
          <DataGridTranslatedColumnHeader
            labelKey="Status"
            keyPrefix={KEY_SALES_ORDER_REPORT_PAGE}
            params={params}
          />
        ),
      },
      {
        field: 'bookedDuringPeriod',
        headerName: 'Booked During Period',
        flex: 1,
        minWidth: 140,
        renderHeader: (params) => (
          <DataGridTranslatedColumnHeader
            labelKey="Booked During Period"
            keyPrefix={KEY_SALES_ORDER_REPORT_PAGE}
            params={params}
          />
        ),
        renderCell: (
          params: GridRenderCellParams<SalesOrderReportRow, number>,
        ) => <span>{formatDate(params.value ?? 0)}</span>,
      },
      {
        field: 'billedDuringPeriod',
        headerName: 'Billed During Period',
        flex: 1,
        minWidth: 140,
        renderHeader: (params) => (
          <DataGridTranslatedColumnHeader
            labelKey="Billed During Period"
            keyPrefix={KEY_SALES_ORDER_REPORT_PAGE}
            params={params}
          />
        ),
        renderCell: (
          params: GridRenderCellParams<SalesOrderReportRow, number>,
        ) => <span>{formatDate(params.value ?? 0)}</span>,
      },
      {
        field: 'expectedFinishDate',
        headerName: 'Expected Finish Date',
        flex: 1,
        minWidth: 150,
        renderHeader: (params) => (
          <DataGridTranslatedColumnHeader
            labelKey="Expected Finish Date"
            keyPrefix={KEY_SALES_ORDER_REPORT_PAGE}
            params={params}
          />
        ),
        renderCell: (
          params: GridRenderCellParams<SalesOrderReportRow, number>,
        ) => <span>{formatDate(params.value ?? 0)}</span>,
      },
      {
        field: 'lineItems',
        headerName: 'Line Items',
        flex: 1,
        minWidth: 100,
        type: 'number',
        renderHeader: (params) => (
          <DataGridTranslatedColumnHeader
            labelKey="Line Items"
            keyPrefix={KEY_SALES_ORDER_REPORT_PAGE}
            params={params}
          />
        ),
        renderCell: (
          params: GridRenderCellParams<SalesOrderReportRow, number>,
        ) => <span>{params.value ?? ''}</span>,
      },
      {
        field: 'cost',
        headerName: 'Cost',
        flex: 1,
        minWidth: 120,
        type: 'number',
        renderHeader: (params) => (
          <DataGridTranslatedColumnHeader
            labelKey="Cost"
            keyPrefix={KEY_SALES_ORDER_REPORT_PAGE}
            params={params}
          />
        ),
        renderCell: (
          params: GridRenderCellParams<SalesOrderReportRow, number>,
        ) => <span>{formatCurrency(params.value ?? 0)}</span>,
      },
    ],
    [],
  );

  const persistedPrefs = usePersistedDataGridColumnPreferences({
    tableId: TABLE_ID,
    columns: baseColumns,
    apiRef,
    leadingGridColumnFields: EMPTY_LEADING_GRID_COLUMNS,
  });

  const columns = useMemo(
    () => persistedPrefs.applyPersistedPreferences(baseColumns),
    [baseColumns, persistedPrefs.applyPersistedPreferences],
  );

  return (
    <Box
      sx={{
        flex: 1,
        minHeight: 0,
        bgcolor: '#fff',
        p: { xs: 2, md: 4 },
        overflow: 'auto',
      }}
    >
      <Paper
        elevation={3}
        sx={{
          minHeight: 640,
          maxWidth: 1160,
          mx: 'auto',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        <Box sx={{ px: 2, py: 1, display: 'flex', alignItems: 'center' }}>
          <Typography variant="h5" color="primary" sx={{ fontWeight: 600 }}>
            {t('Sales Order Report')}
          </Typography>
        </Box>
        <Divider />

        <Box
          sx={{
            p: { xs: 2, md: 5 },
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', md: '1.4fr 1fr auto' },
              gap: 3,
              alignItems: 'start',
            }}
          >
            <Box>
              <FormControl>
                <FormLabel
                  sx={{ typography: 'body2', color: 'text.secondary' }}
                >
                  {t('Generate Report by')}
                </FormLabel>
                <RadioGroup
                  row
                  value={reportBy}
                  onChange={(event) =>
                    handleReportByChange(event.target.value as ReportBy)
                  }
                >
                  <FormControlLabel
                    value="vendor"
                    control={<Radio size="small" />}
                    label={t('Vendor')}
                  />
                  <FormControlLabel
                    value="customer"
                    control={<Radio size="small" />}
                    label={t('Customer')}
                  />
                  <FormControlLabel
                    value="item"
                    control={<Radio size="small" />}
                    label={t('Item')}
                  />
                </RadioGroup>
              </FormControl>

              {reportBy === 'vendor' && (
                <>
                  <Autocomplete
                    size="small"
                    sx={{ mt: 1 }}
                    loading={loadingEntities}
                    options={vendors}
                    value={selectedVendor}
                    getOptionLabel={(option) =>
                      `${option.code} - ${option.details.companyName}`
                    }
                    isOptionEqualToValue={(option, value) =>
                      option.VendorId === value.VendorId
                    }
                    onChange={(_, value) => setSelectedVendor(value)}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label={t('Search for Vendor...')}
                        InputProps={{
                          ...params.InputProps,
                          startAdornment: <SearchIcon color="action" />,
                          endAdornment: (
                            <>
                              {loadingEntities ? (
                                <CircularProgress color="inherit" size={18} />
                              ) : null}
                              {params.InputProps.endAdornment}
                            </>
                          ),
                        }}
                      />
                    )}
                  />
                  <Link
                    component="button"
                    underline="hover"
                    sx={{ mt: 1, ml: 1, fontSize: 14 }}
                    onClick={() =>
                      dispatch(
                        openSearchVendorModal({
                          multiSelect: false,
                          onImport: (selectedVendors) => {
                            const selected = selectedVendors[0];
                            if (selected) {
                              setSelectedVendor(selected);
                            }
                          },
                        }),
                      )
                    }
                  >
                    {t('Browse Vendor')}
                  </Link>
                </>
              )}

              {reportBy === 'customer' && (
                <>
                  <Autocomplete
                    size="small"
                    sx={{ mt: 1 }}
                    loading={loadingEntities}
                    options={customers}
                    value={selectedCustomer}
                    getOptionLabel={(option) =>
                      `${option.code} - ${option.details.companyName}`
                    }
                    isOptionEqualToValue={(option, value) =>
                      option.CustomerId === value.CustomerId
                    }
                    onChange={(_, value) => setSelectedCustomer(value)}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label={t('Search for Customer...')}
                        InputProps={{
                          ...params.InputProps,
                          startAdornment: <SearchIcon color="action" />,
                          endAdornment: (
                            <>
                              {loadingEntities ? (
                                <CircularProgress color="inherit" size={18} />
                              ) : null}
                              {params.InputProps.endAdornment}
                            </>
                          ),
                        }}
                      />
                    )}
                  />
                  <Link
                    component="button"
                    underline="hover"
                    sx={{ mt: 1, ml: 1, fontSize: 14 }}
                    onClick={() =>
                      dispatch(
                        openSearchCustomerModal({
                          multiSelect: false,
                          onImport: (selectedCustomers) => {
                            const selected = selectedCustomers[0];
                            if (selected) {
                              setSelectedCustomer(selected);
                            }
                          },
                        }),
                      )
                    }
                  >
                    {t('Browse Customer')}
                  </Link>
                </>
              )}

              {reportBy === 'item' && (
                <>
                  <Autocomplete
                    size="small"
                    sx={{ mt: 1 }}
                    loading={loadingEntities}
                    options={items}
                    value={selectedItem}
                    getOptionLabel={(option) =>
                      `${option.details.vendorItemNumber} - ${option.details.description}`
                    }
                    isOptionEqualToValue={(option, value) =>
                      option.ItemId === value.ItemId
                    }
                    onChange={(_, value) => setSelectedItem(value)}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label={t('Search for Item...')}
                        InputProps={{
                          ...params.InputProps,
                          startAdornment: <SearchIcon color="action" />,
                          endAdornment: (
                            <>
                              {loadingEntities ? (
                                <CircularProgress color="inherit" size={18} />
                              ) : null}
                              {params.InputProps.endAdornment}
                            </>
                          ),
                        }}
                      />
                    )}
                  />
                  <Link
                    component="button"
                    underline="hover"
                    sx={{ mt: 1, ml: 1, fontSize: 14 }}
                    onClick={() =>
                      dispatch(
                        openSearchItemModal({
                          multiSelect: false,
                          onImport: (selectedItems) => {
                            const selected = selectedItems[0];
                            if (selected) {
                              setSelectedItem(selected);
                            }
                          },
                        }),
                      )
                    }
                  >
                    {t('Browse Item')}
                  </Link>
                </>
              )}
            </Box>

            <Box>
              <FormControl>
                <FormLabel
                  sx={{ typography: 'body2', color: 'text.secondary' }}
                >
                  {t('Filter dates by')}
                </FormLabel>
                <RadioGroup
                  row
                  value={dateFilterBy}
                  onChange={(event) =>
                    setDateFilterBy(event.target.value as DateFilterBy)
                  }
                >
                  <FormControlLabel
                    value="orderDate"
                    control={<Radio size="small" />}
                    label={t('Order Date')}
                  />
                  <FormControlLabel
                    value="expectedShipDate"
                    control={<Radio size="small" />}
                    label={t('Expected Ship Date')}
                  />
                </RadioGroup>
              </FormControl>
              <Stack
                direction={{ xs: 'column', sm: 'row' }}
                spacing={1}
                sx={{ mt: 1 }}
              >
                <LocalizationProvider dateAdapter={AdapterDayjs}>
                  <DatePicker
                    label={t('From')}
                    value={dateFrom ? dayjs(dateFrom) : null}
                    onChange={(value: Dayjs | null) =>
                      setDateFrom(
                        value?.isValid() ? value.format('YYYY-MM-DD') : '',
                      )
                    }
                    slotProps={{ textField: { size: 'small' } }}
                  />
                  <DatePicker
                    label={t('To')}
                    value={dateTo ? dayjs(dateTo) : null}
                    onChange={(value: Dayjs | null) =>
                      setDateTo(
                        value?.isValid() ? value.format('YYYY-MM-DD') : '',
                      )
                    }
                    slotProps={{ textField: { size: 'small' } }}
                  />
                </LocalizationProvider>
              </Stack>
            </Box>

            <Stack alignItems={{ xs: 'stretch', md: 'flex-end' }} spacing={3}>
              <Link
                component="button"
                underline="hover"
                onClick={handleClearFilters}
                sx={{ fontSize: 14 }}
              >
                {t('Clear Filters')}
              </Link>
              <Button
                variant="contained"
                size="small"
                disabled={loadingReport}
                onClick={handleGenerate}
              >
                {loadingReport ? t('Generating') : t('Generate')}
              </Button>
            </Stack>
          </Box>

          <Box
            sx={{
              mt: 4,
              display: 'flex',
              alignItems: 'center',
              gap: 2,
              flexWrap: 'wrap',
            }}
          >
            <Button
              variant="contained"
              size="small"
              disabled={!report || downloading}
              onClick={handleDownload}
              startIcon={
                downloading ? (
                  <CircularProgress size={16} color="inherit" />
                ) : (
                  <DownloadOutlinedIcon />
                )
              }
            >
              {downloading ? t('Downloading') : t('Download')}
            </Button>
          </Box>

          <Box sx={{ mt: 2, height: 480, width: '100%' }}>
            <DataGridPro
              apiRef={apiRef}
              columnHeaderHeight={56}
              sx={{
                border: 'none',
                '.MuiDataGrid-row:nth-of-type(even):not(.Mui-selected)': {
                  backgroundColor: '#ECECEC',
                },
                '.MuiDataGrid-row.Mui-selected': {
                  backgroundColor: (theme) =>
                    hexToRGBA(theme.palette.primary.main, 0.19),
                },
                '.MuiDataGrid-columnHeaderTitle': {
                  fontWeight: 'bold',
                  whiteSpace: 'normal',
                  lineHeight: 1.2,
                },
                '& .MuiDataGrid-cell:focus-within, & .MuiDataGrid-cell:focus': {
                  outline: 'none !important',
                },
              }}
              rows={rows.map((row, index) => ({
                ...row,
                id: `${row.vendorCode}-${index}`,
              }))}
              loading={loadingReport}
              columns={columns}
              columnVisibilityModel={persistedPrefs.columnVisibilityModel}
              onColumnVisibilityModelChange={
                persistedPrefs.onColumnVisibilityModelChange
              }
              onColumnWidthChange={persistedPrefs.onColumnWidthChange}
              onColumnOrderChange={persistedPrefs.onColumnOrderChange}
              pagination
              paginationMode="server"
              paginationModel={paginationModel}
              onPaginationModelChange={(model) => {
                setPaginationModel(model);
                loadReport(buildReportInput(model.page, model.pageSize));
              }}
              rowCount={report?.total ?? 0}
              pageSizeOptions={ROWS_PER_PAGE_OPTIONS}
              disableRowSelectionOnClick
              disableColumnFilter
              autoHeight={false}
              localeText={{
                noRowsLabel: report
                  ? t('No sales order report rows found')
                  : t('Generate the report to view sales orders'),
              }}
            />
          </Box>
        </Box>
      </Paper>
    </Box>
  );
}
